import React, { useRef, useState, useEffect } from 'react';
import { Camera, Upload, Loader2, ScanBarcode, CheckCircle2, Scan, FileText, ArrowRight, RefreshCw } from 'lucide-react';
import { compressImage } from '../utils/imageCompression';

interface ImageInputProps {
  onImagesSelected: (images: string[], mimeType: string) => void;
  isLoading: boolean;
  progress: number;
  statusMessage: string;
  ocrText?: string[];
  mode: 'shelf' | 'precision';
  scanType?: 'quick' | 'pro';
}

const ImageInput: React.FC<ImageInputProps> = ({
  onImagesSelected,
  isLoading,
  progress,
  statusMessage,
  ocrText = [],
  mode,
  scanType = 'pro'
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Precision Mode State
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [pageImage, setPageImage] = useState<string | null>(null);
  const [step, setStep] = useState<'cover' | 'page' | 'ready'>('cover');

  // Reset state when mode changes
  useEffect(() => {
    setCoverImage(null);
    setPageImage(null);
    setStep('cover');
    setErrorMessage(null);
  }, [mode]);

  const handleFile = async (file: File) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please upload a valid image file (JPG, PNG, WEBP).');
      setTimeout(() => setErrorMessage(null), 4000);
      return;
    }

    try {
      const { base64, mimeType } = await compressImage(file);

      if (mode === 'shelf') {
        // Immediate Send for Shelf Mode
        onImagesSelected([base64], mimeType);
      } else {
        // Precision Mode Logic
        if (step === 'cover') {
          setCoverImage(base64);
          if (scanType === 'quick') {
            setStep('ready');
          } else {
            setStep('page');
          }
        } else if (step === 'page') {
          setPageImage(base64);
          setStep('ready');
        }
      }
    } catch (error) {
      console.error("Error processing image:", error);
      setErrorMessage("Failed to process image. Please try another file.");
      setTimeout(() => setErrorMessage(null), 4000);
    }
  };

  const handlePrecisionSubmit = () => {
    if (coverImage) {
      if (mode === 'precision' && scanType === 'quick') {
        // QUICK MODE: Send front only
        onImagesSelected([coverImage], 'image/jpeg');
      } else if (coverImage && pageImage) {
        // PRO MODE: Send both
        onImagesSelected([coverImage, pageImage], 'image/jpeg');
      }
    }
  };

  const resetPrecision = () => {
    setCoverImage(null);
    setPageImage(null);
    setStep('cover');
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  // Render Loading State
  if (isLoading) {
    return (
      <div className="w-full max-w-xl mx-auto -mt-6 relative z-10 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-blue-100 flex flex-col items-center text-center">
          {/* Same loading UI as before but maybe updated text */}
          <div className="relative w-20 h-20 mb-6">
            <svg className="w-full h-full transform -rotate-90">
              <circle cx="40" cy="40" r="36" className="text-slate-100" strokeWidth="8" fill="none" stroke="currentColor" />
              <circle cx="40" cy="40" r="36" className="text-blue-600 transition-all duration-500 ease-out" strokeWidth="8" fill="none" stroke="currentColor" strokeDasharray="226" strokeDashoffset={226 - (226 * progress) / 100} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-bold text-blue-600">{progress}%</span>
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">{statusMessage}</h3>

          {/* OCR Preview */}
          {ocrText && ocrText.length > 0 && (
            <div className="mt-6 w-full text-left">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Detected Text</p>
              <div className="flex flex-wrap gap-1.5 justify-center">
                {ocrText.slice(0, 5).map((text, i) => (
                  <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md animate-fade-in">{text}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // PRECISION MODE UI
  if (mode === 'precision') {
    return (
      <div className="w-full max-w-xl mx-auto -mt-6 relative z-10 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-indigo-100">
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 text-xs font-bold rounded-xl text-center">
              {errorMessage}
            </div>
          )}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-indigo-900">Mint Inspection</h3>
            <div className="flex gap-2 text-xs font-medium">
              <span className={`px-2 py-1 rounded-full ${step === 'cover' ? 'bg-indigo-600 text-white' : coverImage ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>1. Front</span>
              {scanType === 'pro' && (
                <span className={`px-2 py-1 rounded-full ${step === 'page' ? 'bg-indigo-600 text-white' : pageImage ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>2. Back</span>
              )}
            </div>
          </div>

          {step === 'ready' ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center gap-4 mb-4">
                {/* Thumbnails */}
                <div className="w-24 h-32 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border border-slate-200">
                  <img src={`data:image/jpeg;base64,${coverImage}`} className="w-full h-full object-cover" />
                </div>
                <div className="w-24 h-32 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border border-slate-200">
                  <img src={`data:image/jpeg;base64,${pageImage}`} className="w-full h-full object-cover" />
                </div>
              </div>
              <button
                onClick={handlePrecisionSubmit}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-indigo-200 transition-all flex items-center justify-center gap-2"
              >
                <Scan className="w-5 h-5" />
                Analyze Card
              </button>
              <button onClick={resetPrecision} className="text-sm text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1 mx-auto">
                <RefreshCw className="w-3 h-3" /> Reset
              </button>
            </div>
          ) : (
            <div className="text-center space-y-6">
              <div className="p-6 bg-indigo-50 rounded-full w-24 h-24 mx-auto flex items-center justify-center text-indigo-500 mb-4 animate-pulse">
                {step === 'cover' ? <Camera className="w-10 h-10" /> : <FileText className="w-10 h-10" />}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">
                  {step === 'cover' ? "Scan Card Front" : "Scan Card Back"}
                </h2>
                <p className="text-slate-500 text-sm">
                  {step === 'cover' ? "Capture the player and team clearly." : "Find the year and stats info."}
                </p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => fileInputRef.current?.click()} className="flex-1 bg-white border-2 border-indigo-100 hover:border-indigo-300 text-indigo-700 py-3 rounded-xl font-medium transition-colors">
                  Upload
                </button>
                <button onClick={() => cameraInputRef.current?.click()} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-medium transition-colors">
                  Camera
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Hidden Inputs */}
        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={(e) => e.target.files && handleFile(e.target.files[0])} />
        <input type="file" ref={cameraInputRef} className="hidden" accept="image/*" capture="environment" onChange={(e) => e.target.files && handleFile(e.target.files[0])} />
      </div>
    );
  }

  // DEFAULT SHELF MODE (Original)
  return (
    <div className="w-full max-w-xl mx-auto -mt-6 relative z-10 px-4">
      <div
        className={`bg-white rounded-2xl shadow-xl p-8 border-2 transition-all duration-200 ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-100'}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div className="flex flex-col items-center justify-center text-center space-y-4">
          {errorMessage && (
            <div className="w-full mb-2 p-3 bg-red-100 border border-red-200 text-red-700 text-xs font-bold rounded-xl text-center">
              {errorMessage}
            </div>
          )}
          <div className="p-4 bg-blue-100 rounded-full text-blue-600">
            <Camera className="w-8 h-8" />
          </div>

          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-slate-800">Scan Your Books</h3>
            <p className="text-slate-500 text-sm">Take a photo of a single book, a shelf, or a barcode.</p>
          </div>

          <div className="flex gap-3 w-full pt-2 flex-col sm:flex-row">
            <button onClick={() => fileInputRef.current?.click()} className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-xl font-medium transition-colors">
              <Upload className="w-5 h-5" /> Upload Photo
            </button>
            <button onClick={() => cameraInputRef.current?.click()} className="flex-1 flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-900 text-white py-3 px-4 rounded-xl font-medium transition-colors">
              <ScanBarcode className="w-5 h-5" /> Scan ISBN
            </button>
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={(e) => e.target.files && handleFile(e.target.files[0])} />
            <input type="file" ref={cameraInputRef} className="hidden" accept="image/*" capture="environment" onChange={(e) => e.target.files && handleFile(e.target.files[0])} />
          </div>
          <p className="text-xs text-slate-400">Supports JPG, PNG, WEBP, HEIC</p>
        </div>
      </div>
    </div>
  );
};

export default ImageInput;